import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { WebCaptureService } from '../../../services/web-capture/web-capture.service';

type ResultTab =
  | 'scan'
  | 'screenshot'
  | 'selectors'
  | 'intel'
  | 'social'
  | 'site'
  | 'metadata'
  | 'source'
  | 'links';

const SEV_ORDER = ['critical', 'high', 'medium', 'low', 'info'];

const RISK_LABELS: Record<string, string> = {
  critical: 'Critical risk',
  high: 'High risk',
  medium: 'Medium risk',
  low: 'Low risk',
  safe: 'No strong indicators',
};

type SelectorSearchType = 'email' | 'phone' | 'username';

type SelectorGroup = {
  icon: string;
  label: string;
  hits: any[];
  searchType?: SelectorSearchType;
};

@Component({
  selector: 'app-web-capture-result',
  standalone: true,
  imports: [CommonModule, FormsModule, HeaderComponent, SidebarComponent],
  templateUrl: './web-capture-result.component.html',
  styleUrl: './web-capture-result.component.css',
})
export class WebCaptureResultComponent implements OnInit {
  @ViewChild('scanStack') scanStack?: ElementRef<HTMLElement>;

  webCaptureId = '';
  isLoading = true;
  noResultsFound = false;
  captureData: any = null;
  resultId = '';
  historyMeta: any = null;
  activeTab: ResultTab = 'screenshot';
  notes = '';
  initialNotes = '';
  savingNotes = false;
  deleting = false;
  copiedUrl = false;
  copiedHash: string | null = null;
  copiedHtml = false;
  openMirrorPageKey: string | null = null;
  exportingZip = false;
  exportingPdf = false;
  similarLoading = false;
  similarLoaded = false;
  similarMatches: any[] = [];
  similarPhash: string | null = null;

  readonly helpTips = {
    integrityHashes:
      'Cryptographic SHA-256 fingerprints of the captured screenshot, HTML source, and metadata. The composite hash covers everything together. Any later change to the evidence would produce a different hash — this is what makes the capture tamper-evident and court-ready.',
    threatFindings:
      'Automated indicators detected during the scan, ranked by severity. Each finding explains a specific phishing or security signal observed on the target.',
    securityHeaders:
      'Checks for standard protective HTTP headers (HSTS, CSP, X-Frame-Options, etc.). Missing headers indicate a weak or hastily deployed site.',
    redirectChain:
      'The full sequence of HTTP redirects followed from the original URL to the final page. Multi-hop redirects through unrelated domains are a common evasion technique.',
    selectorsSummary:
      'Investigative selectors automatically pulled from the captured page(s): contact details, IP addresses, cryptocurrency wallets, social handles, and any keywords you flagged on your watchlist. Use these to pivot to other cases and connect related activity.',
    keywordHits:
      'Terms from the watchlist you provided at capture time that were found in the page\'s visible text, with how many times each appeared.',
    threatFeeds:
      'The captured host/URL is checked against free abuse blocklists (abuse.ch URLhaus, Spamhaus DBL, SURBL). \'Listed\' means a feed flags it as malicious; \'unavailable\' means the feed could not be reached at capture time.',
    brandImpersonation:
      'Checks whether the page mentions or mimics a well-known brand while NOT being hosted on that brand\'s official domain — a common phishing pattern. Higher confidence means more corroborating signals (hostname, title, page text, and a credential form).',
    favicon:
      'The site icon and its SHA-256 hash. Favicon hashes can be pivoted on to find other sites (often part of the same phishing kit) that reuse the exact same icon.',
    loginForms:
      'Every form on the page and where it submits. A password form that posts to a different domain or over insecure HTTP is a strong credential-harvesting indicator.',
    socialMedia:
      'When the target is a social-media page, the platform, account handle and content type are detected from the URL, and profile/post details are pulled from the page\'s Open Graph / card metadata. No platform API keys are used.',
    ssl:
      'Details of the site\'s HTTPS certificate — who issued it, when it is valid, and its fingerprint. Phishing sites often use very recently issued free certificates or mismatched subject names.',
    dns:
      'The domain\'s DNS records at capture time (A/AAAA addresses, mail servers, name servers, TXT, CNAME). Establishes where the domain resolved and who hosts it.',
    whois:
      'Domain registration data (registrar, creation/expiry dates, status). A very recently registered domain is a common phishing indicator.',
    httpHeaders:
      'The raw HTTP headers the server returned. Useful for identifying the server software, caching, cookies, and security configuration.',
    metaTags:
      'Metadata declared in the page\'s HTML head (title, description, Open Graph, generator). Can reveal the toolkit or template used to build a phishing page.',
    htmlSource:
      'The exact HTML the page returned at capture time, preserved verbatim. This is the primary evidence of the page\'s content and is covered by the HTML SHA-256 hash.',
    extractedLinks:
      'Every hyperlink found on the page, flagged as internal (INT) or external (EXT). External links often point to credential-harvesting endpoints or redirectors.',
    investigatorNotes:
      'Your private annotations for this capture. Record context and observations. These are saved with the evidence and included in the exported report and ZIP.',
    similarCaptures:
      'Other captures whose screenshot looks visually similar, matched with a perceptual image hash. Useful for finding pages from the same phishing kit or campaign even when the URL differs.',
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private webCaptureService: WebCaptureService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id') || '';
      if (!id) {
        this.isLoading = false;
        this.noResultsFound = true;
        return;
      }
      if (id === this.webCaptureId && this.captureData) {
        return;
      }
      this.webCaptureId = id;
      this.loadResults();
    });
  }

  loadResults() {
    this.isLoading = true;
    this.noResultsFound = false;
    this.captureData = null;
    this.webCaptureService.getResults(this.webCaptureId).subscribe({
      next: (response: any) => {
        const raw = response?.data;
        const rows = raw?.data_list || [];
        this.historyMeta = raw?.history || null;
        this.notes = raw?.notes || '';
        this.initialNotes = this.notes;
        if (rows.length) {
          const row = rows[0];
          this.resultId = row.result_id || row.dataValues?.result_id || '';
          this.captureData = row.capture_data || row.dataValues?.capture_data || {};
          this.noResultsFound = false;
        } else {
          this.captureData = null;
          this.noResultsFound = true;
        }
        this.activeTab = this.scan ? 'scan' : 'screenshot';
        this.similarLoaded = false;
        this.similarMatches = [];
        this.similarPhash =
          this.captureData?.screenshotPhash ||
          this.captureData?.intel?.screenshotPhash ||
          null;
        this.isLoading = false;
        this.scheduleEqualizeScanStack();
      },
      error: () => {
        this.captureData = null;
        this.noResultsFound = true;
        this.isLoading = false;
        this.toastr.error('Unable to load capture results.');
      },
    });
  }

  onBack() {
    this.router.navigate(['/web-capture']);
  }

  setTab(tab: ResultTab) {
    this.activeTab = tab;
    if (tab === 'intel') {
      this.loadSimilarCaptures();
    }
    if (tab === 'scan') {
      this.scheduleEqualizeScanStack();
    }
  }

  /** Equalize Security headers + Redirect chain to the taller card's real content height. */
  private scheduleEqualizeScanStack() {
    setTimeout(() => this.equalizeScanStack(), 0);
    setTimeout(() => this.equalizeScanStack(), 50);
  }

  private equalizeScanStack() {
    const stack = this.scanStack?.nativeElement;
    if (!stack || this.activeTab !== 'scan') return;

    const panels = Array.from(
      stack.querySelectorAll(':scope > .wc-panel'),
    ) as HTMLElement[];
    if (panels.length < 2) return;

    // Reset to natural content size before measuring
    panels.forEach((panel) => {
      panel.style.height = 'auto';
      panel.style.minHeight = '0';
      panel.style.maxHeight = 'none';
    });

    const naturalHeights = panels.map((panel) => {
      const heading = panel.querySelector('h4') as HTMLElement | null;
      const body = panel.querySelector('.wc-panel-body') as HTMLElement | null;
      const styles = getComputedStyle(panel);
      const padY =
        (parseFloat(styles.paddingTop) || 0) +
        (parseFloat(styles.paddingBottom) || 0);
      const headingH = heading ? heading.offsetHeight : 0;
      const headingMb = heading
        ? parseFloat(getComputedStyle(heading).marginBottom) || 0
        : 0;
      const bodyH = body ? body.scrollHeight : panel.scrollHeight;
      return Math.ceil(headingH + headingMb + bodyH + padY);
    });

    const matched = Math.max(...naturalHeights, 0);
    if (!matched) return;

    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    // Match --wc-stack-card-max-h (14.5rem) so ~5 header rows fit
    const perCardMax = Math.floor(14.5 * rem);
    const height = Math.min(matched, perCardMax);

    panels.forEach((panel) => {
      panel.style.height = `${height}px`;
      panel.style.minHeight = `${height}px`;
      panel.style.maxHeight = `${height}px`;
    });
  }

  loadSimilarCaptures() {
    if (!this.webCaptureId || this.similarLoaded || this.similarLoading) return;
    this.similarLoading = true;
    this.webCaptureService.getSimilarCaptures(this.webCaptureId).subscribe({
      next: (response: any) => {
        const data = response?.data || {};
        this.similarMatches = Array.isArray(data.matches) ? data.matches : [];
        this.similarPhash =
          data.phash ||
          this.captureData?.screenshotPhash ||
          this.captureData?.intel?.screenshotPhash ||
          null;
        this.similarLoaded = true;
        this.similarLoading = false;
      },
      error: () => {
        this.similarMatches = [];
        this.similarLoaded = true;
        this.similarLoading = false;
      },
    });
  }

  openSimilarCapture(id: string) {
    if (!id) return;
    this.router.navigate(['/web-capture-result', id]);
  }

  get scan() {
    return this.captureData?.securityScan || null;
  }

  get ssl() {
    return this.captureData?.sslInfo || {};
  }

  get sslAltNames(): string {
    const names = this.ssl?.subjectAltNames;
    if (!Array.isArray(names) || !names.length) return '—';
    return names.join(', ');
  }

  get dnsRecords() {
    return this.captureData?.dnsRecords || {};
  }

  get whois() {
    return this.captureData?.whoisData || {};
  }

  get httpHeaders(): Record<string, any> {
    return this.captureData?.httpHeaders || {};
  }

  get metaTags(): any[] {
    return Array.isArray(this.captureData?.metaTags) ? this.captureData.metaTags : [];
  }

  get links(): any[] {
    return Array.isArray(this.captureData?.links) ? this.captureData.links : [];
  }

  get selectors() {
    return this.captureData?.selectors || null;
  }

  get intel() {
    return this.captureData?.intel || null;
  }

  get social() {
    return this.captureData?.social || null;
  }

  get crawlSummary() {
    return this.captureData?.crawlSummary || null;
  }

  get sitePages(): any[] {
    return Array.isArray(this.captureData?.sitePages) ? this.captureData.sitePages : [];
  }

  get hasSelectors(): boolean {
    const s = this.selectors;
    return !!s && ((s.totalEntities ?? 0) > 0 || (s.keywordHits?.length ?? 0) > 0);
  }

  get hasIntel(): boolean {
    return !!this.intel;
  }

  get isSocial(): boolean {
    return !!this.social?.isSocial;
  }

  get hasCrawl(): boolean {
    return !!this.crawlSummary && this.sitePages.length > 0;
  }

  get reference(): string {
    return this.historyMeta?.reference || this.captureData?.reference || '';
  }

  get captureStatus(): string {
    return (
      this.historyMeta?.capture_status ||
      this.captureData?.status ||
      'unknown'
    );
  }

  get capturedAt(): string | null {
    return this.historyMeta?.created_on || this.captureData?.capturedAt || null;
  }

  get riskLevel(): string {
    return this.captureData?.riskLevel || this.scan?.riskLevel || 'safe';
  }

  get riskScore(): number {
    const s = this.captureData?.riskScore ?? this.scan?.riskScore;
    return typeof s === 'number' ? s : 0;
  }

  get riskLabel(): string {
    return RISK_LABELS[this.riskLevel] || RISK_LABELS['safe'];
  }

  get riskCounts() {
    return this.scan?.counts || {};
  }

  get screenshotSrc(): string | null {
    const b64 = this.captureData?.screenshotBase64;
    if (!b64) return null;
    return b64.startsWith('data:') ? b64 : `data:image/png;base64,${b64}`;
  }

  get notesDirty(): boolean {
    return this.notes !== this.initialNotes;
  }

  get sortedFindings(): any[] {
    const findings = Array.isArray(this.scan?.findings) ? [...this.scan.findings] : [];
    return findings.sort(
      (a, b) => SEV_ORDER.indexOf(a.severity) - SEV_ORDER.indexOf(b.severity),
    );
  }

  get securityHeaders(): any[] {
    return Array.isArray(this.scan?.securityHeaders) ? this.scan.securityHeaders : [];
  }

  get redirectChain(): any[] {
    return Array.isArray(this.scan?.redirectChain) ? this.scan.redirectChain : [];
  }

  get selectorGroups(): SelectorGroup[] {
    const s = this.selectors || {};
    const groups: SelectorGroup[] = [
      { icon: 'fa-envelope', label: 'Email addresses', hits: s.emails || [], searchType: 'email' },
      { icon: 'fa-phone', label: 'Phone numbers', hits: s.phones || [], searchType: 'phone' },
      { icon: 'fa-server', label: 'IP addresses', hits: s.ips || [] },
      { icon: 'fa-wallet', label: 'Bitcoin addresses', hits: s.cryptoBtc || [] },
      { icon: 'fa-wallet', label: 'Ethereum addresses', hits: s.cryptoEth || [] },
      { icon: 'fa-at', label: 'Social handles', hits: s.socialHandles || [], searchType: 'username' },
    ];
    return groups.filter((g) => Array.isArray(g.hits) && g.hits.length > 0);
  }

  get keywordHits(): any[] {
    return Array.isArray(this.selectors?.keywordHits) ? this.selectors.keywordHits : [];
  }

  get threatFeeds(): any[] {
    return Array.isArray(this.intel?.threatFeeds) ? this.intel.threatFeeds : [];
  }

  get formFlags(): string[] {
    return Array.isArray(this.intel?.formFlags) ? this.intel.formFlags : [];
  }

  get loginForms(): any[] {
    return Array.isArray(this.intel?.forms) ? this.intel.forms : [];
  }

  get brandImpersonation() {
    return this.intel?.brandImpersonation || { detected: false };
  }

  get favicon() {
    return this.intel?.favicon || {};
  }

  get crawlAssets() {
    return this.crawlSummary?.assets || {};
  }

  get hashEntries(): { label: string; value: string; highlight?: boolean }[] {
    return [
      { label: 'Screenshot', value: this.captureData?.screenshotHash },
      { label: 'HTML source', value: this.captureData?.htmlHash },
      { label: 'Metadata', value: this.captureData?.metadataHash },
      { label: 'Composite', value: this.captureData?.compositeHash, highlight: true },
    ].filter((h) => !!h.value);
  }

  fmtBytes(n?: number | null): string {
    if (!n || n <= 0) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  }

  sevBadgeClass(severity: string): string {
    const map: Record<string, string> = {
      critical: 'sev-critical',
      high: 'sev-high',
      medium: 'sev-medium',
      low: 'sev-low',
      info: 'sev-info',
    };
    return map[severity] || 'sev-info';
  }

  threatBadgeClass(status: string): string {
    if (status === 'listed') return 'threat-listed';
    if (status === 'clean') return 'threat-clean';
    return 'threat-unknown';
  }

  confidenceBadgeClass(c: string): string {
    if (c === 'high') return 'sev-critical';
    if (c === 'medium') return 'sev-medium';
    return 'sev-low';
  }

  hopStatusClass(status: number): string {
    if (status >= 300 && status < 400) return 'hop-redirect';
    if (status >= 400) return 'hop-error';
    return 'hop-ok';
  }

  mirrorPageKey(page: any): string {
    return page.id || page.url;
  }

  toggleMirrorPage(page: any) {
    const key = this.mirrorPageKey(page);
    this.openMirrorPageKey = this.openMirrorPageKey === key ? null : key;
  }

  isMirrorOpen(page: any): boolean {
    return this.openMirrorPageKey === this.mirrorPageKey(page);
  }

  async copyText(text: string, message = 'Copied') {
    try {
      await navigator.clipboard.writeText(text);
      this.toastr.success(message);
    } catch {
      this.toastr.error('Unable to copy to clipboard.');
    }
  }

  canSearchSelector(searchType?: SelectorSearchType): boolean {
    return searchType === 'email' || searchType === 'phone' || searchType === 'username';
  }

  searchSelector(searchType: SelectorSearchType | undefined, rawValue: string): void {
    if (!this.canSearchSelector(searchType) || !rawValue?.trim()) {
      return;
    }

    const searchQuery = this.resolveSelectorSearchQuery(searchType!, rawValue);
    if (!searchQuery) {
      return;
    }

    const routeMap: Record<string, string> = {
      email: '/osint-email-search',
      phone: '/osint-number-search',
      username: '/osint-username-search',
    };

    const route = routeMap[searchType!];
    if (!route) {
      return;
    }

    this.router.navigate([route], {
      state: { autoSearch: true, searchQuery },
    });
  }

  private resolveSelectorSearchQuery(searchType: SelectorSearchType, rawValue: string): string | null {
    const trimmed = rawValue.trim();
    if (!trimmed || trimmed === '—' || trimmed === 'N/A') {
      return null;
    }

    if (searchType === 'username') {
      return this.extractSocialUsername(trimmed);
    }

    if (searchType === 'email') {
      return trimmed.toLowerCase();
    }

    if (searchType === 'phone') {
      return trimmed;
    }

    return null;
  }

  private extractSocialUsername(value: string): string | null {
    const colonIdx = value.indexOf(':');
    let handle = colonIdx >= 0 ? value.slice(colonIdx + 1).trim() : value.trim();
    if (handle.includes('/')) {
      const parts = handle.split('/').filter(Boolean);
      handle = parts[parts.length - 1];
    }
    handle = handle.replace(/^@/, '').trim();
    return handle || null;
  }

  copyUrl() {
    const url = this.historyMeta?.url || this.captureData?.url || '';
    this.copyText(url, 'URL copied');
    this.copiedUrl = true;
    setTimeout(() => (this.copiedUrl = false), 1500);
  }

  copyHash(value: string) {
    this.copyText(value, 'Hash copied');
    this.copiedHash = value;
    setTimeout(() => (this.copiedHash = null), 1500);
  }

  copyHtmlSource() {
    const html = this.captureData?.htmlSource || '';
    if (!html) return;
    this.copyText(html, 'HTML source copied');
    this.copiedHtml = true;
    setTimeout(() => (this.copiedHtml = false), 1500);
  }

  downloadScreenshot() {
    if (!this.screenshotSrc) return;
    const ref = this.reference || 'capture';
    const a = document.createElement('a');
    a.href = this.screenshotSrc;
    a.download = `${ref}-screenshot.png`;
    a.click();
  }

  exportZip() {
    if (!this.webCaptureId || this.exportingPdf) return;
    this.exportingZip = true;
    this.webCaptureService.exportArchive(this.webCaptureId).subscribe({
      next: (res) => {
        this.saveBlob(res.body, this.buildExportFileName('zip'))
        this.exportingZip = false;
        this.toastr.success('Evidence archive downloaded.');
      },
      error: () => {
        this.exportingZip = false;
        this.toastr.error('Unable to export archive.');
      },
    });
  }

  exportPdf() {
    if (!this.webCaptureId || this.exportingZip) return;
    this.exportingPdf = true;
    this.webCaptureService.exportReport(this.webCaptureId).subscribe({
      next: (res) => {
        this.saveBlob(res.body, this.buildExportFileName('pdf'))
        this.exportingPdf = false;
        this.toastr.success('Evidence report downloaded.');
      },
      error: () => {
        this.exportingPdf = false;
        this.toastr.error('Unable to generate PDF report.');
      },
    });
  }

  private saveBlob(blob: Blob | null, filename: string) {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  private sanitizeFilePart(value: string, maxLen = 80): string {
    return String(value || '')
      .trim()
      .replace(/https?:\/\//gi, '')
      .replace(/^www\./i, '')
      .replace(/[^a-zA-Z0-9._-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, maxLen);
  }

  private relatedExportName(): string {
    const domain = this.historyMeta?.domain || this.captureData?.domain;
    if (domain) return String(domain).replace(/^www\./i, '');
    const url = this.historyMeta?.url || this.captureData?.url;
    if (url) {
      try {
        return new URL(url).hostname.replace(/^www\./i, '');
      } catch {
        // fall through
      }
    }
    return this.captureData?.title || 'capture';
  }

  private formatExportStamp(raw: string | null): string {
    const date = raw ? new Date(raw) : new Date();
    const safe = Number.isNaN(date.getTime()) ? new Date() : date;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${safe.getFullYear()}-${pad(safe.getMonth() + 1)}-${pad(safe.getDate())}_${pad(safe.getHours())}-${pad(safe.getMinutes())}-${pad(safe.getSeconds())}`;
  }

  private buildExportFileName(extension: string): string {
    const name = this.sanitizeFilePart(this.relatedExportName()) || 'capture';
    const reference = this.sanitizeFilePart(this.reference, 40);
    const stamp = this.formatExportStamp(this.capturedAt);
    return `${[name, reference, stamp].filter(Boolean).join('_')}.${extension}`;
  }

  saveNotes() {
    if (!this.resultId || !this.notesDirty) return;
    this.savingNotes = true;
    this.webCaptureService
      .updateCapture({
        result_id: this.resultId,
        notes: this.notes.trim() || undefined,
      })
      .subscribe({
        next: () => {
          this.initialNotes = this.notes;
          this.savingNotes = false;
          this.toastr.success('Notes saved');
        },
        error: () => {
          this.savingNotes = false;
          this.toastr.error('Failed to save notes.');
        },
      });
  }

  deleteCapture() {
    if (!this.webCaptureId || !confirm('Delete this capture permanently? This cannot be undone.')) {
      return;
    }
    this.deleting = true;
    this.webCaptureService.deleteHistory([this.webCaptureId]).subscribe({
      next: () => {
        this.toastr.success('Capture deleted');
        this.router.navigate(['/web-capture']);
      },
      error: () => {
        this.deleting = false;
        this.toastr.error('Failed to delete capture.');
      },
    });
  }

  statusBadgeClass(): string {
    const s = this.captureStatus?.toLowerCase();
    if (s === 'completed') return 'status-completed';
    if (s === 'failed') return 'status-failed';
    return 'status-pending';
  }

  objectKeys(obj: Record<string, any>): string[] {
    return Object.keys(obj || {});
  }

  mxRecords(): string[] {
    const mx = this.dnsRecords?.mx;
    if (!Array.isArray(mx)) return [];
    return mx.map((m: any) => `${m.priority} ${m.exchange}`);
  }

  dnsList(label: string, arr: any): { label: string; values: string[] } | null {
    if (!Array.isArray(arr) || !arr.length) return null;
    return { label, values: arr.map((x) => String(x)) };
  }

  get dnsLists() {
    return [
      this.dnsList('A', this.dnsRecords?.a),
      this.dnsList('AAAA', this.dnsRecords?.aaaa),
      this.dnsList('MX', this.mxRecords()),
      this.dnsList('NS', this.dnsRecords?.ns),
      this.dnsList('CNAME', this.dnsRecords?.cname),
      this.dnsList('TXT', this.dnsRecords?.txt),
    ].filter(Boolean) as { label: string; values: string[] }[];
  }
}
